import React from "react";

import { DownloadIcon } from "../../assets";
import useDownloadTemplate, {
  DownloadTemplateParams,
} from "../../hooks/useDownloadTemplate";
import { IMPORT_TEMPLATE_LABELS } from "../../utils/constants";
import { Loader } from "../loaders/loaders";

interface DownloadTemplateButtonProps extends DownloadTemplateParams {
  /** Overrides the default label of the template type. */
  label?: string;
  className?: string;
  /** Button text while the template is being generated. */
  loadingLabel?: string;
  disabled?: boolean;
  /**
   * Renders the app loader over the page while the template is generated.
   * Opt out when the screen already drives its own loader for the same action.
   */
  showLoader?: boolean;
}

/**
 * Downloads an agent scoped import template (agent rate, agent hub, FSC, service
 * zip, additional charges …).
 *
 * Pass `agentId` and let the component resolve the agent number, or pass
 * `agentNumber` directly when the screen already has it. `hubId` is only needed
 * — and only sent — for hub scoped templates such as service zip.
 *
 * @example
 * <DownloadTemplateButton
 *   templateType={IMPORT_TEMPLATE_TYPE.AGENT_HUB}
 *   agentId={agentId}
 * />
 */
const DownloadTemplateButton: React.FC<DownloadTemplateButtonProps> = ({
  templateType,
  agentId,
  agentNumber,
  hubId,
  label,
  className = "btn btn__white w_fitContent",
  loadingLabel = "Downloading...",
  disabled = false,
  showLoader = true,
}) => {
  const { downloadTemplate, isDownloading } = useDownloadTemplate();

  const handleClick = () =>
    downloadTemplate({ templateType, agentId, agentNumber, hubId });

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handleClick}
        disabled={disabled || isDownloading}
      >
        <DownloadIcon />
        {isDownloading ? loadingLabel : label || IMPORT_TEMPLATE_LABELS[templateType]}
      </button>
      {showLoader && isDownloading && <Loader />}
    </>
  );
};

export default DownloadTemplateButton;
